import { useTranslations } from 'next-intl'
import { MouseEvent } from 'react'

import { isNilOrEmptyString, useFormatDate } from '@/shared/lib'
import { Badge, Icon } from '@/shared/ui'

import { DeleteButton } from './delete-button'
import { NoContent } from './no-content'
import { History } from '../../model/search-history-store/history'
import { useSearchHistoryStore } from '../../model/search-history-store/store'
import { Item, List } from '../popup/common'

const HistoryBadges = ({ child }: { child: History['child'] }) => {
  if (child.length === 0) return null

  return (
    <ul className="flex gap-1">
      {child[0].type === 'badge' &&
        child.map(item => (
          <li key={item.name} className="flex">
            <Badge variant="solid" color={item.color}>
              {item.shortName}
            </Badge>
          </li>
        ))}
      {child[0].type === 'icon' &&
        child.map(item => (
          <li key={item.name} className="flex">
            <Icon name={item.name} size={18} className={item.color} />
          </li>
        ))}
    </ul>
  )
}

const DateRange = ({
  startDate,
  endDate,
}: {
  startDate: string | null
  endDate: string | null
}) => {
  const { formatDate } = useFormatDate()

  if (isNilOrEmptyString(startDate) || isNilOrEmptyString(endDate)) return null

  return (
    <p className="typo-caption1 font-medium text-gray-500">
      {formatDate({ date: startDate, format: 'yyyy.MM.dd' })}~
      {formatDate({ date: endDate, format: 'yyyy.MM.dd' })}
    </p>
  )
}

type Props = {
  onItemClick: (history: History) => void
}

export const SearchHistoryList = ({ onItemClick }: Props) => {
  const t = useTranslations('map_search')

  const searchHistory = useSearchHistoryStore(state => state.searchHistory)
  const checkSearchHistoryExist = useSearchHistoryStore(
    state => state.checkSearchHistoryExist,
  )
  const searchHistoryActions = useSearchHistoryStore(state => state.actions)
  const hasSearchHistory = checkSearchHistoryExist()

  const handleItemClick = (history: History) => () => {
    onItemClick(history)
  }

  const handleDeleteItem = (id: string) => (event: MouseEvent) => {
    event.stopPropagation()
    searchHistoryActions.deleteSearchHistory(id)
  }

  const handleDeleteAll = () => {
    searchHistoryActions.clearSearchHistory()
  }

  return (
    <List title={t('recent_search.title')} className="relative w-[400px]">
      {hasSearchHistory && <DeleteButton onClick={handleDeleteAll} />}
      {(() => {
        if (!hasSearchHistory)
          return <NoContent>{t('recent_search.empty')}</NoContent>

        return searchHistory.map(history => (
          <Item
            key={history.id}
            onClick={handleItemClick(history)}
            data-item-type="search-history"
          >
            <div className="space-y-1">
              <HistoryBadges child={history.child} />
              <p>{history.name || t('recent_search.default_keyword')}</p>
              <DateRange
                startDate={history.startDate}
                endDate={history.endDate}
              />
            </div>
            <div role="button" onClick={handleDeleteItem(history.id)}>
              <Icon name="close" size={16} className="text-gray-500" />
              <span className="sr-only">{t('recent_search.button.clear')}</span>
            </div>
          </Item>
        ))
      })()}
    </List>
  )
}
